import { ApplicationConfig, inject, mergeApplicationConfig, NgZone, provideAppInitializer } from '@angular/core';
import { provideRmaFeatureFlagsBrowser } from '@rma/generic/ng/feature-flags';
import { provideRmaInstance } from '@rma/generic/ng/instance';
import { DeviceIdentificationService, provideDeviceIdentification } from '@rma/generic/ng/util';
import { provideRmaPostHog, provideRmaPostHogRouteTracking } from '@rma/generic/util/posthog';
import { Environment, IS_PRODUCTION, PlatformService, provideRmaEnvironment } from '@rma/public/generic/util/environment';
import { FeatureFlagUserIdentificationService } from '@rma/public/generic/util/feature-flags';
import { HotjarIdentificationService, HotjarScriptService } from '@rma/public/generic/util/tracking/hotjar';
import { provideRmaMetaPixelRouteTracking } from '@rma/public/generic/util/tracking/meta-pixel';
import { environment as devEnvironment } from '../environment/environment';
import { commonConfig } from './app.config.common';
import { provideRmaPostHogCanary } from './providers/posthog-canary.provider';

declare let environment: Environment;

const env = IS_PRODUCTION ? environment : devEnvironment;

const browserConfig: ApplicationConfig = {
  providers: [
    provideRmaInstance(env.tenantCode, env.type),
    // this overrides the one that is injected from ssr, so we need to make sure it is done only in the browser version
    provideRmaEnvironment(env),
    // Cookie based, so browser only. The apex domain makes the fallback id one device across the
    // apex and findmyagent.<domain>, which holds because that app passes the same domain. PostHog
    // is bootstrapped with it here but not there, so the two hosts remain separate people.
    provideDeviceIdentification(env.domain),
    // The LaunchDarkly client is browser-only; the server has its own provider in app.config.server.ts.
    provideRmaFeatureFlagsBrowser(env.launchDarklyClientId, FeatureFlagUserIdentificationService),
    provideRmaMetaPixelRouteTracking(),
    // No ErrorHandler: TrackJS owns errors here, and this app carries far more anonymous traffic
    // than find-my-agent.
    provideRmaPostHog(env.tracking.posthogProjectToken, env.tracking.posthogHost, 'public', PlatformService, {
      // Reading it is also what writes the cookie the SSR render looks for on the next request.
      deviceId: DeviceIdentificationService,
      config: {
        capture_pageview: false,
        capture_pageleave: true,
        capture_exceptions: false,
        disable_session_recording: true,
        person_profiles: 'identified_only',
      },
    }),
    provideRmaPostHogRouteTracking(),
    provideRmaPostHogCanary(),
    // Temporary: app-wide Hotjar for the concierge "find me an agent" consult test. Inlined rather
    // than using provideRmaHotJar() because that provider is shared with the survey app, which opts
    // into it already — reviving its body would silently start recording survey sessions too.
    // Browser-only config so the SSR render can never emit the script tag.
    provideAppInitializer(() => {
      const platformService = inject(PlatformService);
      const hotjarScriptService = inject(HotjarScriptService);
      const hotjarIdentificationService = inject(HotjarIdentificationService);
      const zone = inject(NgZone);

      // Identification is gated on the id as well as the platform: without one appendScript() bails,
      // window.hj never exists, and identify() would log a console error on every user emission.
      if (platformService.isTrackable && env.tracking.hotjarId) {
        zone.runOutsideAngular(() => {
          hotjarScriptService.appendScript();
          hotjarIdentificationService.initialiseIdentification();
        });
      }
    }),
  ],
};

export const config = mergeApplicationConfig(commonConfig, browserConfig);
